import { useEffect, useState } from 'react';

export type ScrollRegionFocus = {
  ref: (node: HTMLElement | null) => void;
  /** Spread on the scrolling element. Empty while its content fits. */
  focusProps: { tabIndex?: number; role?: 'region'; 'aria-labelledby'?: string };
};

/**
 * Makes a scrolling element focusable while its content overflows, so a keyboard can scroll it.
 * Safari, unlike Chrome and Firefox, doesn't put a scroll area in the tab order on its own. Pass
 * `labelId` only when that element renders, as it also makes the element a named region.
 */
const useScrollRegionFocus = (labelId?: string, enabled = true): ScrollRegionFocus => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    if (!node || !enabled || typeof ResizeObserver === 'undefined') {
      setOverflows(false);
      return undefined;
    }
    const check = (): void => setOverflows(node.scrollHeight > node.clientHeight);
    const observer = new ResizeObserver(check);
    observer.observe(node);
    Array.from(node.children).forEach((child) => observer.observe(child));
    check();
    return () => observer.disconnect();
  }, [node, enabled]);

  return {
    ref: setNode,
    focusProps: overflows
      ? { tabIndex: 0, ...(labelId && { role: 'region', 'aria-labelledby': labelId }) }
      : {},
  };
};

export default useScrollRegionFocus;
